import React, { useState, useEffect } from 'react';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  PhoneIcon,
  EnvelopeIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  UserGroupIcon,
  BanknotesIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import * as XLSX from 'xlsx';
import { supabase } from '../../lib/supabase';
import { adminUpdateApplicationStatus, adminConfirmPayment, adminExtendDeadline, adminBatchExtendDeadline, adminRestoreApplication } from '../../services/adminApplicationService';
import {
  adminApproveRefund,
  adminRejectRefund,
  adminCompleteRefund,
  adminInitiateRefund,
  getRefundStatusLabel,
  getRefundStatusColor,
  Refund,
} from '../../services/refundService';
import { selectInChunks, selectAllPages } from '../../utils/chunkedIn';

interface ApplicationData {
  id: string;
  application_number: string;
  student_id: string;
  student_name: string;
  status: 'registered' | 'waiting' | 'cancelled';
  waiting_number: number | null;
  payment_status: 'pending' | 'completed' | 'expired' | 'cancelled' | 'refunded' | null;
  payment_amount: number | null;
  payment_deadline: string | null;
  paid_at: string | null;
  created_at: string;
  // 프로필 정보
  user_phone: string;
  user_email: string;
  username: string;
  birthdate: string | null;
  gender: 'male' | 'female' | null;
  address: string | null;
  // 보호자 정보 (14세 미만)
  guardian_name: string | null;
  guardian_phone: string | null;
  // 학교/학년 정보 (14세 미만)
  school: string | null;
  grade: string | null;
  // 강좌 정보
  program_name: string;
  class_name: string;
  course_start_date: string;
  course_end_date: string;
  // 환불 정보
  refund?: Refund;
}

type TabType = 'all' | 'pending' | 'completed' | 'waiting' | 'refund' | 'cancelled';

const StudentManagement: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [applications, setApplications] = useState<ApplicationData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProgram, setSelectedProgram] = useState('all');
  const [programs, setPrograms] = useState<{id: string, name: string}[]>([]);
  const [activeTab, setActiveTab] = useState<TabType>('all');

  // 학교/학년 필터
  const [selectedSchool, setSelectedSchool] = useState('all');
  const [selectedGrade, setSelectedGrade] = useState('all');
  const [schools, setSchools] = useState<string[]>([]);
  const [exporting, setExporting] = useState(false);

  // 관리 기능 관련 state
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [confirmingPaymentId, setConfirmingPaymentId] = useState<string | null>(null);
  const [processingRefundId, setProcessingRefundId] = useState<string | null>(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingRefund, setRejectingRefund] = useState<{ id: string; studentName: string; applicationId: string } | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // 관리자 직접 환불 모달
  const [showInitiateRefundModal, setShowInitiateRefundModal] = useState(false);
  const [initiateRefundTarget, setInitiateRefundTarget] = useState<{ applicationId: string; studentName: string; programName: string; amount: number } | null>(null);
  const [initiateRefundReason, setInitiateRefundReason] = useState('');
  const [initiateRefundNote, setInitiateRefundNote] = useState('');

  // 상세보기 모달
  const [selectedApplication, setSelectedApplication] = useState<ApplicationData | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // 결제기한 연장 모달
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [extendingApplication, setExtendingApplication] = useState<ApplicationData | null>(null);
  const [extendHours, setExtendHours] = useState(48);
  const [extendingId, setExtendingId] = useState<string | null>(null);

  // 일괄 기한연장 모달
  const [showBatchExtendModal, setShowBatchExtendModal] = useState(false);
  const [batchDateFrom, setBatchDateFrom] = useState('');
  const [batchDateTo, setBatchDateTo] = useState('');
  const [batchTargets, setBatchTargets] = useState<ApplicationData[]>([]);
  const [batchSelectedIds, setBatchSelectedIds] = useState<Set<string>>(new Set());
  const [batchNewDate, setBatchNewDate] = useState('');
  const [batchNewTime, setBatchNewTime] = useState('18:00');
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [batchSearched, setBatchSearched] = useState(false);

  // 취소 복구 처리
  const [restoringId, setRestoringId] = useState<string | null>(null);

  useEffect(() => {
    loadApplications();
    loadPrograms();
  }, []);

  const loadPrograms = async () => {
    const { data } = await supabase
      .from('programs')
      .select('id, name')
      .order('name');

    if (data) {
      setPrograms(data);
    }
  };

  const loadApplications = async () => {
    setLoading(true);
    try {
      // 1. 모든 신청 목록 조회 (취소 포함)
      //    PostgREST가 1회 1,000행에서 끊으므로 range로 끝까지 넘긴다.
      const { data: apps, error } = await selectAllPages<any>((from, to) => supabase
        .from('applications')
        .select(`
          id,
          application_number,
          student_id,
          student_name,
          class_id,
          status,
          waiting_number,
          payment_status,
          payment_amount,
          payment_deadline,
          paid_at,
          created_at,
          program_classes:class_id (
            id,
            class_name,
            course_start_date,
            course_end_date,
            program_id,
            programs:program_id (
              id,
              name
            )
          )
        `)
        .order('created_at', { ascending: false })
        .order('id')
        .range(from, to));

      if (error) {
        console.error('Error loading applications:', error);
        return;
      }

      if (!apps || apps.length === 0) {
        setApplications([]);
        return;
      }

      // 2. student_id 목록으로 profiles에서 사용자 정보 조회
      const studentIds = Array.from(new Set(apps.map(a => a.student_id).filter(Boolean)));

      const { data: profiles, error: profilesError } = await selectInChunks<any>(
        studentIds,
        slice => supabase
          .from('profiles')
          .select('id, username, name, email, phone, birthdate, gender, address, guardian_name, guardian_phone, school, grade')
          .in('id', slice)
      );
      if (profilesError) console.error('Error loading student profiles:', profilesError);

      // 3. 환불 정보 조회
      const applicationIds = apps.map(a => a.id);
      const { data: refunds, error: refundsError } = await selectInChunks<Refund>(
        applicationIds,
        slice => supabase
          .from('refunds')
          .select('*')
          .in('application_id', slice)
      );
      if (refundsError) console.error('Error loading refunds:', refundsError);

      const refundMap: Record<string, Refund> = {};
      refunds?.forEach(r => {
        refundMap[r.application_id] = r as Refund;
      });

      // 4. 사용자 정보 매핑
      const profileMap: Record<string, {
        name: string;
        email: string;
        phone: string;
        username: string;
        birthdate: string | null;
        gender: 'male' | 'female' | null;
        address: string | null;
        guardian_name: string | null;
        guardian_phone: string | null;
        school: string | null;
        grade: string | null;
      }> = {};
      const schoolSet = new Set<string>();
      profiles?.forEach(p => {
        profileMap[p.id] = {
          name: p.name || p.username || '',
          email: p.email || '',
          phone: p.phone || '',
          username: p.username || '',
          birthdate: p.birthdate || null,
          gender: p.gender || null,
          address: p.address || null,
          guardian_name: p.guardian_name || null,
          guardian_phone: p.guardian_phone || null,
          school: p.school || null,
          grade: p.grade || null
        };
        if (p.school) schoolSet.add(p.school);
      });
      setSchools(Array.from(schoolSet).sort());

      // 5. 데이터 변환
      const transformedApps: ApplicationData[] = apps.map((app: any) => {
        const profile = profileMap[app.student_id] || {
          name: '',
          email: '',
          phone: '',
          username: '',
          birthdate: null,
          gender: null,
          address: null,
          guardian_name: null,
          guardian_phone: null,
          school: null,
          grade: null
        };
        const programClass = app.program_classes;

        return {
          id: app.id,
          application_number: app.application_number,
          student_id: app.student_id,
          student_name: app.student_name || profile.name,
          status: app.status,
          waiting_number: app.waiting_number,
          payment_status: app.payment_status,
          payment_amount: app.payment_amount,
          payment_deadline: app.payment_deadline,
          paid_at: app.paid_at,
          created_at: app.created_at,
          user_phone: profile.phone,
          user_email: profile.email,
          username: profile.username,
          birthdate: profile.birthdate,
          gender: profile.gender,
          address: profile.address,
          guardian_name: profile.guardian_name,
          guardian_phone: profile.guardian_phone,
          school: profile.school,
          grade: profile.grade,
          program_name: programClass?.programs?.name || '',
          class_name: programClass?.class_name || '',
          course_start_date: programClass?.course_start_date || '',
          course_end_date: programClass?.course_end_date || '',
          refund: refundMap[app.id]
        };
      });

      setApplications(transformedApps);
    } catch (error) {
      console.error('Error loading applications:', error);
    } finally {
      setLoading(false);
    }
  };

  // 탭별 필터링
  const getFilteredByTab = (apps: ApplicationData[]) => {
    switch (activeTab) {
      case 'pending':
        return apps.filter(a =>
          a.status === 'registered' &&
          (a.payment_status === 'pending' || a.payment_status === 'expired')
        );
      case 'completed':
        return apps.filter(a =>
          a.status === 'registered' &&
          a.payment_status === 'completed'
        );
      case 'waiting':
        return apps.filter(a => a.status === 'waiting');
      case 'refund':
        return apps.filter(a =>
          a.refund &&
          (a.refund.refund_status === 'requested' || a.refund.refund_status === 'approved')
        );
      case 'cancelled':
        return apps.filter(a => a.status === 'cancelled');
      default:
        // 전체 탭에서는 취소된 신청 제외
        return apps.filter(a => a.status !== 'cancelled');
    }
  };

  // 검색 및 프로그램 필터링
  const filteredApplications = getFilteredByTab(applications).filter(app => {
    const matchesSearch =
      app.student_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.user_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.user_phone.includes(searchTerm) ||
      app.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.application_number.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesProgram = selectedProgram === 'all' || app.program_name === selectedProgram;
    const matchesSchool = selectedSchool === 'all' || app.school === selectedSchool;
    const matchesGrade = selectedGrade === 'all' || app.grade === selectedGrade;

    return matchesSearch && matchesProgram && matchesSchool && matchesGrade;
  });

  // 학년 옵션
  const gradeOptions = [
    '초등1학년', '초등2학년', '초등3학년', '초등4학년', '초등5학년', '초등6학년',
    '중등1학년', '중등2학년', '중등3학년', '기타'
  ];

  // 엑셀 다운로드
  const handleExportExcel = async () => {
    if (exporting || filteredApplications.length === 0) return;

    setExporting(true);

    try {
      // 상태 라벨 변환 함수
      const getStatusLabel = (status: string) => {
        switch (status) {
          case 'registered': return '접수완료';
          case 'waiting': return '대기중';
          case 'cancelled': return '취소';
          default: return status;
        }
      };

      const getPaymentStatusLabel = (status: string | null) => {
        switch (status) {
          case 'completed': return '결제완료';
          case 'pending': return '입금대기';
          case 'expired': return '기한만료';
          case 'cancelled': return '결제취소';
          case 'refunded': return '환불완료';
          default: return '-';
        }
      };

      const getGenderLabel = (gender: string | null) => {
        switch (gender) {
          case 'male': return '남';
          case 'female': return '여';
          default: return '';
        }
      };

      // 엑셀 데이터 생성
      const excelData = filteredApplications.map((app, index) => ({
        '번호': index + 1,
        '신청번호': app.application_number,
        '강좌명': app.program_name,
        '반': app.class_name !== '기본반' ? app.class_name : '',
        '수강생명': app.student_name,
        '생년월일': app.birthdate || '',
        '성별': getGenderLabel(app.gender),
        '학교': app.school || '',
        '학년': app.grade || '',
        '연락처': app.user_phone || '',
        '보호자명': app.guardian_name || '',
        '보호자연락처': app.guardian_phone || '',
        '이메일': app.user_email || '',
        '주소': app.address || '',
        '신청상태': getStatusLabel(app.status),
        '결제상태': getPaymentStatusLabel(app.payment_status),
        '결제금액': app.payment_amount || 0,
        '결제기한': app.payment_deadline ? new Date(app.payment_deadline).toLocaleString('ko-KR') : '',
        '신청일시': new Date(app.created_at).toLocaleString('ko-KR'),
      }));

      // 워크시트 생성
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // 컬럼 너비 설정
      worksheet['!cols'] = [
        { wch: 5 },   // 번호
        { wch: 15 },  // 신청번호
        { wch: 20 },  // 강좌명
        { wch: 10 },  // 반
        { wch: 10 },  // 수강생명
        { wch: 12 },  // 생년월일
        { wch: 5 },   // 성별
        { wch: 15 },  // 학교
        { wch: 10 },  // 학년
        { wch: 15 },  // 연락처
        { wch: 10 },  // 보호자명
        { wch: 15 },  // 보호자연락처
        { wch: 25 },  // 이메일
        { wch: 30 },  // 주소
        { wch: 10 },  // 신청상태
        { wch: 10 },  // 결제상태
        { wch: 10 },  // 결제금액
        { wch: 20 },  // 결제기한
        { wch: 20 },  // 신청일시
      ];

      // 워크북 생성
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, '수강신청목록');

      // 파일명 생성
      const now = new Date();
      const fileName = `수강신청목록_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}.xlsx`;

      // 다운로드
      XLSX.writeFile(workbook, fileName);
    } catch (error) {
      console.error('Excel export error:', error);
      alert('엑셀 다운로드 중 오류가 발생했습니다.');
    } finally {
      setExporting(false);
    }
  };

  // 탭별 카운트
  const getCounts = () => {
    const pending = applications.filter(a =>
      a.status === 'registered' &&
      (a.payment_status === 'pending' || a.payment_status === 'expired')
    ).length;
    const completed = applications.filter(a =>
      a.status === 'registered' &&
      a.payment_status === 'completed'
    ).length;
    const waiting = applications.filter(a => a.status === 'waiting').length;
    const refund = applications.filter(a =>
      a.refund &&
      (a.refund.refund_status === 'requested' || a.refund.refund_status === 'approved')
    ).length;
    const cancelled = applications.filter(a => a.status === 'cancelled').length;
    const allExceptCancelled = applications.filter(a => a.status !== 'cancelled').length;

    return { all: allExceptCancelled, pending, completed, waiting, refund, cancelled };
  };

  const counts = getCounts();

  // 입금확인 처리
  const handleConfirmPayment = async (applicationId: string, studentName: string) => {
    if (confirmingPaymentId) return;

    const confirmed = window.confirm(
      `"${studentName}"님의 입금을 확인 처리하시겠습니까?\n\n결제 상태가 '완료'로 변경됩니다.`
    );

    if (!confirmed) return;

    setConfirmingPaymentId(applicationId);

    try {
      const result = await adminConfirmPayment(applicationId);

      if (result.success) {
        alert('입금 확인이 완료되었습니다.');
        await loadApplications();
      } else {
        alert(`오류: ${result.error?.message || '입금확인 처리에 실패했습니다.'}`);
      }
    } catch (error) {
      console.error('Error confirming payment:', error);
      alert('입금확인 처리 중 오류가 발생했습니다.');
    } finally {
      setConfirmingPaymentId(null);
    }
  };

  // 수강 취소 처리
  const handleCancelApplication = async (applicationId: string, studentName: string) => {
    if (processingId) return;

    const confirmed = window.confirm(
      `"${studentName}"님의 수강을 취소하시겠습니까?\n\n※ 대기 1번이 자동으로 수강 확정됩니다.`
    );

    if (!confirmed) return;

    setProcessingId(applicationId);

    try {
      const response = await adminUpdateApplicationStatus({
        application_id: applicationId,
        new_status: 'cancelled',
      });

      if (response.success) {
        alert(
          response.data?.promoted
            ? `수강이 취소되었습니다.\n대기자 ${response.data.promoted.student_name}님이 자동으로 등록되었습니다.`
            : '수강이 취소되었습니다.'
        );
        await loadApplications();
      } else {
        alert(`오류: ${response.error?.message || '수강 취소에 실패했습니다.'}`);
      }
    } catch (error) {
      console.error('Error cancelling application:', error);
      alert('수강 취소 중 오류가 발생했습니다.');
    } finally {
      setProcessingId(null);
    }
  };

  // 대기자 수강 확정
  const handlePromoteWaiting = async (applicationId: string, studentName: string) => {
    if (processingId) return;

    const confirmed = window.confirm(
      `"${studentName}"님을 수강 확정하시겠습니까?\n\n⚠️ 정원을 초과할 수 있습니다.`
    );

    if (!confirmed) return;

    setProcessingId(applicationId);

    try {
      const response = await adminUpdateApplicationStatus({
        application_id: applicationId,
        new_status: 'registered',
      });

      if (response.success) {
        alert('수강 확정되었습니다.');
        await loadApplications();
      } else {
        alert(`오류: ${response.error?.message || '수강 확정에 실패했습니다.'}`);
      }
    } catch (error) {
      console.error('Error promoting waiting:', error);
      alert('수강 확정 중 오류가 발생했습니다.');
    } finally {
      setProcessingId(null);
    }
  };

  // 대기 취소 (대기자 → 취소, 자동 승격 없음 / 안내 문자 미발송)
  const handleCancelWaiting = async (applicationId: string, studentName: string) => {
    if (processingId) return;

    const confirmed = window.confirm(
      `"${studentName}"님의 대기를 취소하시겠습니까?\n\n· 대기자 목록에서 제외됩니다.\n· 안내 문자는 발송되지 않습니다.\n· 필요 시 '취소' 탭에서 복구할 수 있습니다.`
    );

    if (!confirmed) return;

    setProcessingId(applicationId);

    try {
      const response = await adminUpdateApplicationStatus({
        application_id: applicationId,
        new_status: 'cancelled',
      });

      if (response.success) {
        alert('대기가 취소되었습니다.');
        await loadApplications();
      } else {
        alert(`오류: ${response.error?.message || '대기 취소에 실패했습니다.'}`);
      }
    } catch (error) {
      console.error('Error cancelling waiting:', error);
      alert('대기 취소 중 오류가 발생했습니다.');
    } finally {
      setProcessingId(null);
    }
  };

  // 환불 승인 처리
  const handleApproveRefund = async (refundId: string, studentName: string) => {
    if (processingRefundId) return;

    const confirmed = window.confirm(
      `"${studentName}"님의 환불을 승인하시겠습니까?\n\n승인 후 실제 환불 처리가 완료되면 '환불 완료' 처리를 해주세요.`
    );

    if (!confirmed) return;

    setProcessingRefundId(refundId);

    try {
      const result = await adminApproveRefund(refundId);

      if (result.success) {
        alert('환불이 승인되었습니다.');
        await loadApplications();
      } else {
        alert(`오류: ${result.error?.message || '환불 승인에 실패했습니다.'}`);
      }
    } catch (error) {
      console.error('Error approving refund:', error);
      alert('환불 승인 중 오류가 발생했습니다.');
    } finally {
      setProcessingRefundId(null);
    }
  };

  // 환불 거절 모달 열기
  const openRejectModal = (refundId: string, studentName: string, applicationId: string) => {
    setRejectingRefund({ id: refundId, studentName, applicationId });
    setRejectionReason('');
    setShowRejectModal(true);
  };

  // 환불 거절 처리
  const handleRejectRefund = async () => {
    if (!rejectingRefund || !rejectionReason.trim()) {
      alert('거절 사유를 입력해주세요.');
      return;
    }

    setProcessingRefundId(rejectingRefund.id);

    try {
      const result = await adminRejectRefund(rejectingRefund.id, rejectionReason);

      if (result.success) {
        alert('환불이 거절되었습니다.');
        setShowRejectModal(false);
        setRejectingRefund(null);
        setRejectionReason('');
        await loadApplications();
      } else {
        alert(`오류: ${result.error?.message || '환불 거절에 실패했습니다.'}`);
      }
    } catch (error) {
      console.error('Error rejecting refund:', error);
      alert('환불 거절 중 오류가 발생했습니다.');
    } finally {
      setProcessingRefundId(null);
    }
  };

  // 환불 완료 처리
  const handleCompleteRefund = async (refundId: string, studentName: string) => {
    if (processingRefundId) return;

    const confirmed = window.confirm(
      `"${studentName}"님의 환불 완료 처리를 하시겠습니까?\n\n⚠️ 실제 환불 송금이 완료된 후에 처리해주세요.\n- 신청 상태가 '취소'로 변경됩니다.\n- 결제 상태가 '환불'로 변경됩니다.`
    );

    if (!confirmed) return;

    setProcessingRefundId(refundId);

    try {
      const result = await adminCompleteRefund(refundId);

      if (result.success) {
        alert('환불 완료 처리되었습니다.');
        await loadApplications();
      } else {
        alert(`오류: ${result.error?.message || '환불 완료 처리에 실패했습니다.'}`);
      }
    } catch (error) {
      console.error('Error completing refund:', error);
      alert('환불 완료 처리 중 오류가 발생했습니다.');
    } finally {
      setProcessingRefundId(null);
    }
  };

  // 관리자 직접 환불 모달 열기
  const openInitiateRefundModal = (app: ApplicationData) => {
    setInitiateRefundTarget({
      applicationId: app.id,
      studentName: app.student_name,
      programName: app.program_name,
      amount: app.payment_amount || 0,
    });
    setInitiateRefundReason('');
    setInitiateRefundNote('');
    setShowInitiateRefundModal(true);
  };

  // 관리자 직접 환불 처리
  const handleInitiateRefund = async () => {
    if (!initiateRefundTarget || !initiateRefundReason.trim()) {
      alert('환불 사유를 입력해주세요.');
      return;
    }

    setProcessingRefundId(initiateRefundTarget.applicationId);

    try {
      const result = await adminInitiateRefund(
        initiateRefundTarget.applicationId,
        initiateRefundReason,
        initiateRefundNote || undefined
      );

      if (result.success) {
        alert('환불 처리가 완료되었습니다.');
        setShowInitiateRefundModal(false);
        setInitiateRefundTarget(null);
        await loadApplications();
      } else {
        alert(`오류: ${result.error?.message || '환불 처리에 실패했습니다.'}`);
      }
    } catch (error) {
      console.error('Error initiating refund:', error);
      alert('환불 처리 중 오류가 발생했습니다.');
    } finally {
      setProcessingRefundId(null);
    }
  };

  // 결제 기한 연장 모달 열기
  const openExtendModal = (app: ApplicationData) => {
    setExtendingApplication(app);
    setExtendHours(24);
    setShowExtendModal(true);
  };

  // 결제 기한 연장 처리
  const handleExtendDeadline = async () => {
    if (!extendingApplication) return;

    setExtendingId(extendingApplication.id);

    try {
      const newDeadline = new Date();
      newDeadline.setHours(newDeadline.getHours() + extendHours);

      const result = await adminExtendDeadline(extendingApplication.id, newDeadline.toISOString());

      if (result.success) {
        alert(`결제기한이 ${extendHours / 24}일(${extendHours}시간) 연장되었습니다.`);
        setShowExtendModal(false);
        setExtendingApplication(null);
        await loadApplications();
      } else {
        alert(`오류: ${result.error?.message || '결제기한 연장에 실패했습니다.'}`);
      }
    } catch (error) {
      console.error('Error extending deadline:', error);
      alert('결제기한 연장 중 오류가 발생했습니다.');
    } finally {
      setExtendingId(null);
    }
  };

  // 일괄 기한연장 모달 열기
  const openBatchExtendModal = () => {
    const pad = (n: number) => String(n).padStart(2, '0');
    const toLocalDateStr = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    // 오늘 날짜를 기본 값으로 (KST 로컬 기준)
    const today = new Date();
    setBatchDateFrom(toLocalDateStr(today));
    setBatchDateTo(toLocalDateStr(today));
    setBatchTargets([]);
    setBatchSelectedIds(new Set());
    setBatchSearched(false);
    // 내일 날짜를 기본 새 기한으로
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    setBatchNewDate(toLocalDateStr(tomorrow));
    setBatchNewTime('18:00');
    setBatchProcessing(false);
    setShowBatchExtendModal(true);
  };

  // 일괄 기한연장 대상 조회
  const handleBatchSearch = () => {
    if (!batchDateFrom || !batchDateTo) {
      alert('신청일 범위를 선택해주세요.');
      return;
    }
    if (batchDateFrom > batchDateTo) {
      alert('신청일 시작일이 종료일보다 늦습니다.');
      return;
    }

    // 신청일 범위 + registered + pending/expired 조건으로 필터
    const fromDate = new Date(batchDateFrom + 'T00:00:00');
    const toDate = new Date(batchDateTo + 'T23:59:59');

    const targets = applications.filter(app => {
      // 신청 상태: registered만
      if (app.status !== 'registered') return false;
      // 결제 상태: pending 또는 expired만
      if (app.payment_status !== 'pending' && app.payment_status !== 'expired') return false;
      // 프로그램 필터 적용
      if (selectedProgram !== 'all' && app.program_name !== selectedProgram) return false;
      // 신청일 범위
      const createdAt = new Date(app.created_at);
      return createdAt >= fromDate && createdAt <= toDate;
    });

    setBatchTargets(targets);
    setBatchSelectedIds(new Set(targets.map(t => t.id)));
    setBatchSearched(true);
  };

  // 일괄 기한연장 체크박스 토글
  const toggleBatchSelect = (id: string) => {
    setBatchSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // 일괄 기한연장 전체선택 토글
  const toggleBatchSelectAll = () => {
    if (batchSelectedIds.size === batchTargets.length) {
      setBatchSelectedIds(new Set());
    } else {
      setBatchSelectedIds(new Set(batchTargets.map(t => t.id)));
    }
  };

  // 일괄 기한연장 실행
  const handleBatchExtend = async () => {
    if (batchSelectedIds.size === 0) {
      alert('연장할 대상을 선택해주세요.');
      return;
    }
    if (!batchNewDate || !batchNewTime) {
      alert('새 납부기한 날짜와 시간을 입력해주세요.');
      return;
    }

    // KST → ISO 변환 (초 없이 분까지만)
    const newDeadlineKST = new Date(`${batchNewDate}T${batchNewTime}:00+09:00`);
    if (isNaN(newDeadlineKST.getTime())) {
      alert('유효하지 않은 날짜/시간입니다.');
      return;
    }
    if (newDeadlineKST <= new Date()) {
      alert('새 납부기한은 현재 시간 이후로 설정해야 합니다.');
      return;
    }

    const confirmed = window.confirm(
      `선택된 ${batchSelectedIds.size}명에게 납부기한을\n${batchNewDate} ${batchNewTime} 으로 일괄 변경합니다.\n\n진행하시겠습니까?`
    );
    if (!confirmed) return;

    setBatchProcessing(true);
    try {
      const result = await adminBatchExtendDeadline(
        Array.from(batchSelectedIds),
        newDeadlineKST.toISOString()
      );

      if (result.success && result.data) {
        const { succeeded, failed } = result.data;
        alert(`일괄 기한연장 완료\n\n성공: ${succeeded}건${failed > 0 ? `\n실패: ${failed}건` : ''}`);
        setShowBatchExtendModal(false);
        await loadApplications();
      } else {
        alert(`오류: ${result.error?.message || '일괄 기한연장에 실패했습니다.'}`);
      }
    } catch (error) {
      console.error('Batch extend error:', error);
      alert('일괄 기한연장 중 오류가 발생했습니다.');
    } finally {
      setBatchProcessing(false);
    }
  };

  // 취소 신청 복구 처리
  const handleRestoreApplication = async (app: ApplicationData, forceRestore: boolean = false) => {
    if (restoringId) return;

    // 환불 처리된 경우 복구 불가
    if (app.payment_status === 'refunded') {
      alert('환불 처리된 신청은 복구할 수 없습니다.\n신규 신청을 진행해주세요.');
      return;
    }

    if (!forceRestore) {
      const confirmed = window.confirm(
        `"${app.student_name}"님의 신청을 복구하시겠습니까?\n\n복구 시:\n- 신청상태: 접수완료\n- 결제상태: 입금대기\n- 결제기한: 다음 업무일 14시(일·월·휴관일 제외)`
      );
      if (!confirmed) return;
    }

    setRestoringId(app.id);

    try {
      const result = await adminRestoreApplication(app.id, forceRestore);

      if (result.success) {
        alert('신청이 복구되었습니다.');
        await loadApplications();
      } else if (result.requireConfirmation) {
        // 정원 초과 경고
        const forceConfirmed = window.confirm(
          `현재 정원이 꽉 찬 상태입니다.\n(현재: ${result.currentStudents}/${result.capacity}명)\n\n그래도 복구하시겠습니까? 정원을 초과하게 됩니다.`
        );
        if (forceConfirmed) {
          await handleRestoreApplication(app, true);
        }
      } else {
        alert(`오류: ${result.error?.message || '신청 복구에 실패했습니다.'}`);
      }
    } catch (error) {
      console.error('Error restoring application:', error);
      alert('신청 복구 중 오류가 발생했습니다.');
    } finally {
      setRestoringId(null);
    }
  };

  // 결제 상태 뱃지
  const getPaymentStatusBadge = (paymentStatus: string | null, paymentDeadline: string | null) => {
    if (!paymentStatus) {
      return <span className="text-xs text-gray-400">-</span>;
    }

    const isExpired = paymentDeadline && new Date(paymentDeadline) < new Date();

    switch (paymentStatus) {
      case 'completed':
        return <span className="inline-block px-2 py-0.5 text-xs font-medium rounded bg-emerald-50 text-emerald-600 border border-emerald-200">결제완료</span>;
      case 'pending':
        return (
          <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${
            isExpired
              ? 'bg-red-50 text-red-600 border border-red-200'
              : 'bg-amber-50 text-amber-600 border border-amber-200'
          }`}>
            {isExpired ? '기한초과' : '입금대기'}
          </span>
        );
      case 'expired':
        return <span className="inline-block px-2 py-0.5 text-xs font-medium rounded bg-red-50 text-red-600 border border-red-200">기한만료</span>;
      case 'cancelled':
        return <span className="inline-block px-2 py-0.5 text-xs font-medium rounded bg-slate-50 text-slate-600 border border-slate-200">결제취소</span>;
      case 'refunded':
        return <span className="inline-block px-2 py-0.5 text-xs font-medium rounded bg-purple-50 text-purple-600 border border-purple-200">환불완료</span>;
      default:
        return <span className="inline-block px-2 py-0.5 text-xs font-medium rounded bg-slate-50 text-slate-600 border border-slate-200">{paymentStatus}</span>;
    }
  };

  // 신청 상태 뱃지
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'registered':
        return <span className="inline-block px-2 py-0.5 text-xs font-medium rounded bg-emerald-50 text-emerald-600 border border-emerald-200">접수완료</span>;
      case 'waiting':
        return <span className="inline-block px-2 py-0.5 text-xs font-medium rounded bg-amber-50 text-amber-600 border border-amber-200">대기중</span>;
      case 'cancelled':
        return <span className="inline-block px-2 py-0.5 text-xs font-medium rounded bg-slate-50 text-slate-600 border border-slate-200">취소</span>;
      default:
        return null;
    }
  };

  // 환불 상태 뱃지
  const getRefundBadge = (refund: Refund | undefined) => {
    if (!refund) return null;

    return (
      <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${getRefundStatusColor(refund.refund_status)}`}>
        {getRefundStatusLabel(refund.refund_status)}
      </span>
    );
  };

  // 상세보기
  const handleViewDetails = (app: ApplicationData) => {
    setSelectedApplication(app);
    setShowDetailModal(true);
  };

  // 결제 기한 포맷
  const formatDeadline = (deadline: string | null) => {
    if (!deadline) return '-';
    const date = new Date(deadline);
    const now = new Date();
    const isExpired = date < now;

    const formatted = date.toLocaleDateString('ko-KR', {
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    });

    return (
      <span className={isExpired ? 'text-red-600 font-medium' : ''}>
        {formatted}
        {isExpired && ' (만료)'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-indigo-600 mx-auto"></div>
          <p className="mt-4 text-sm text-gray-500">신청 목록을 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 mb-1">신청 관리</h1>
          <p className="text-sm text-gray-500">수강 신청 현황을 조회하고 관리합니다.</p>
        </div>
        <button
          onClick={loadApplications}
          className="flex items-center px-3 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          <ArrowPathIcon className="h-4 w-4 mr-1.5" />
          새로고침
        </button>
      </div>

      {/* 탭 네비게이션 */}
      <div className="bg-white rounded-lg border border-gray-200 mb-6">
        <div className="flex overflow-x-auto">
          <button
            onClick={() => setActiveTab('all')}
            className={`flex items-center px-5 py-4 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
              activeTab === 'all'
                ? 'border-indigo-600 text-indigo-600 bg-indigo-50/50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <UserGroupIcon className="h-4 w-4 mr-2" />
            전체
            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
              activeTab === 'all' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'
            }`}>
              {counts.all}
            </span>
          </button>

          <button
            onClick={() => setActiveTab('pending')}
            className={`flex items-center px-5 py-4 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
              activeTab === 'pending'
                ? 'border-amber-500 text-amber-600 bg-amber-50/50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <ClockIcon className="h-4 w-4 mr-2" />
            입금대기
            {counts.pending > 0 && (
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                activeTab === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-amber-100 text-amber-700'
              }`}>
                {counts.pending}
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('completed')}
            className={`flex items-center px-5 py-4 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
              activeTab === 'completed'
                ? 'border-emerald-500 text-emerald-600 bg-emerald-50/50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <CheckCircleIcon className="h-4 w-4 mr-2" />
            결제완료
            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
              activeTab === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'
            }`}>
              {counts.completed}
            </span>
          </button>

          <button
            onClick={() => setActiveTab('waiting')}
            className={`flex items-center px-5 py-4 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
              activeTab === 'waiting'
                ? 'border-blue-500 text-blue-600 bg-blue-50/50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <ExclamationCircleIcon className="h-4 w-4 mr-2" />
            대기자
            {counts.waiting > 0 && (
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                activeTab === 'waiting' ? 'bg-blue-100 text-blue-700' : 'bg-blue-100 text-blue-700'
              }`}>
                {counts.waiting}
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('refund')}
            className={`flex items-center px-5 py-4 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
              activeTab === 'refund'
                ? 'border-purple-500 text-purple-600 bg-purple-50/50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <BanknotesIcon className="h-4 w-4 mr-2" />
            환불처리
            {counts.refund > 0 && (
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                activeTab === 'refund' ? 'bg-purple-100 text-purple-700' : 'bg-purple-100 text-purple-700'
              }`}>
                {counts.refund}
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('cancelled')}
            className={`flex items-center px-5 py-4 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
              activeTab === 'cancelled'
                ? 'border-slate-500 text-slate-600 bg-slate-50/50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
            수강취소
            {counts.cancelled > 0 && (
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                activeTab === 'cancelled' ? 'bg-slate-100 text-slate-700' : 'bg-slate-100 text-slate-600'
              }`}>
                {counts.cancelled}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* 검색 및 필터 */}
      <div className="bg-white rounded-lg border border-gray-200 mb-6 p-4">
        <div className="flex flex-col gap-4">
          {/* 상단: 검색 + 엑셀 다운로드 */}
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="이름, 아이디, 연락처, 신청번호로 검색..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-150"
              />
            </div>

            <button
              onClick={openBatchExtendModal}
              className="flex items-center justify-center px-4 py-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors whitespace-nowrap"
            >
              <ClockIcon className="h-4 w-4 mr-1.5" />
              일괄 기한연장
            </button>

            <button
              onClick={handleExportExcel}
              disabled={exporting || filteredApplications.length === 0}
              className="flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-1.5" />
              {exporting ? '다운로드 중...' : `엑셀 다운로드 (${filteredApplications.length}건)`}
            </button>
          </div>

          {/* 하단: 필터들 */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <FunnelIcon className="h-4 w-4 text-gray-500" />
              <span className="text-xs text-gray-600">필터:</span>
            </div>

            <select
              value={selectedProgram}
              onChange={(e) => setSelectedProgram(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-150"
            >
              <option value="all">전체 강좌</option>
              {programs.map(p => (
                <option key={p.id} value={p.name}>{p.name}</option>
              ))}
            </select>

            <select
              value={selectedSchool}
              onChange={(e) => setSelectedSchool(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-150"
            >
              <option value="all">전체 학교</option>
              {schools.map(school => (
                <option key={school} value={school}>{school}</option>
              ))}
            </select>

            <select
              value={selectedGrade}
              onChange={(e) => setSelectedGrade(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-150"
            >
              <option value="all">전체 학년</option>
              {gradeOptions.map(grade => (
                <option key={grade} value={grade}>{grade}</option>
              ))}
            </select>

            {/* 필터 초기화 버튼 */}
            {(selectedProgram !== 'all' || selectedSchool !== 'all' || selectedGrade !== 'all' || searchTerm) && (
              <button
                onClick={() => {
                  setSelectedProgram('all');
                  setSelectedSchool('all');
                  setSelectedGrade('all');
                  setSearchTerm('');
                }}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                필터 초기화
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 신청 목록 테이블 */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  신청번호
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  수강생
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  연락처
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  강좌
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  신청상태
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  결제상태
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  결제금액
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  결제기한
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  신청일
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  작업
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredApplications.map((app) => (
                <tr
                  key={app.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleViewDetails(app)}
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="text-sm font-mono text-gray-600">{app.application_number}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{app.student_name}</div>
                      <div className="text-xs text-gray-500">{app.username}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-600">{app.user_phone || '-'}</div>
                    {app.guardian_phone && (
                      <div className="text-xs text-blue-600 mt-0.5">
                        <span className="text-gray-400">└</span> 보호자: {app.guardian_name || ''} {app.guardian_phone}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{app.program_name}</div>
                      {app.class_name && app.class_name !== '기본반' && (
                        <div className="text-xs text-gray-500">{app.class_name}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center whitespace-nowrap">
                    <div className="flex flex-col items-center gap-1">
                      {getStatusBadge(app.status)}
                      {app.status === 'waiting' && app.waiting_number && (
                        <span className="text-xs text-gray-500">#{app.waiting_number}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center whitespace-nowrap">
                    <div className="flex flex-col items-center gap-1">
                      {getPaymentStatusBadge(app.payment_status, app.payment_deadline)}
                      {getRefundBadge(app.refund)}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <span className="text-sm font-medium text-gray-900">
                      {app.payment_amount ? `${app.payment_amount.toLocaleString()}원` : '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    {formatDeadline(app.payment_deadline)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="text-sm text-gray-600">
                      {new Date(app.created_at).toLocaleDateString('ko-KR')}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                    <div className="flex justify-center gap-1 flex-wrap">
                      {/* 입금확인 버튼 */}
                      {app.status === 'registered' &&
                        (app.payment_status === 'pending' || app.payment_status === 'expired') && (
                        <button
                          onClick={() => handleConfirmPayment(app.id, app.student_name)}
                          disabled={confirmingPaymentId === app.id}
                          className="px-2.5 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 disabled:opacity-50"
                        >
                          {confirmingPaymentId === app.id ? '...' : '입금확인'}
                        </button>
                      )}

                      {/* 대기자 수강 확정 */}
                      {app.status === 'waiting' && (
                        <button
                          onClick={() => handlePromoteWaiting(app.id, app.student_name)}
                          disabled={processingId === app.id}
                          className="px-2.5 py-1.5 text-xs font-medium text-emerald-600 bg-emerald-50 border border-emerald-200 rounded hover:bg-emerald-100 disabled:opacity-50"
                        >
                          {processingId === app.id ? '...' : '수강확정'}
                        </button>
                      )}

                      {/* 대기 취소 (승격·문자 없음) */}
                      {app.status === 'waiting' && (
                        <button
                          onClick={() => handleCancelWaiting(app.id, app.student_name)}
                          disabled={processingId === app.id}
                          className="px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded hover:bg-gray-100 disabled:opacity-50"
                        >
                          {processingId === app.id ? '...' : '대기 취소'}
                        </button>
                      )}

                      {/* 환불 승인/거절 */}
                      {app.refund?.refund_status === 'requested' && (
                        <>
                          <button
                            onClick={() => handleApproveRefund(app.refund!.id, app.student_name)}
                            disabled={processingRefundId === app.refund!.id}
                            className="px-2.5 py-1.5 text-xs font-medium text-emerald-600 bg-emerald-50 border border-emerald-200 rounded hover:bg-emerald-100 disabled:opacity-50"
                          >
                            승인
                          </button>
                          <button
                            onClick={() => openRejectModal(app.refund!.id, app.student_name, app.id)}
                            disabled={processingRefundId === app.refund!.id}
                            className="px-2.5 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded hover:bg-red-100 disabled:opacity-50"
                          >
                            거절
                          </button>
                        </>
                      )}

                      {/* 환불 완료 */}
                      {app.refund?.refund_status === 'approved' && (
                        <button
                          onClick={() => handleCompleteRefund(app.refund!.id, app.student_name)}
                          disabled={processingRefundId === app.refund!.id}
                          className="px-2.5 py-1.5 text-xs font-medium text-purple-600 bg-purple-50 border border-purple-200 rounded hover:bg-purple-100 disabled:opacity-50"
                        >
                          환불완료
                        </button>
                      )}

                      {/* 관리자 직접 환불 (결제완료 + 유료 + 환불 없음) */}
                      {app.status === 'registered' &&
                        app.payment_status === 'completed' &&
                        (app.payment_amount ?? 0) > 0 &&
                        !app.refund && (
                        <button
                          onClick={() => openInitiateRefundModal(app)}
                          disabled={processingRefundId === app.id}
                          className="px-2.5 py-1.5 text-xs font-medium text-purple-600 bg-purple-50 border border-purple-200 rounded hover:bg-purple-100 disabled:opacity-50"
                        >
                          환불처리
                        </button>
                      )}

                      {/* 수강취소 (수강확정 상태 + 환불 없음) */}
                      {app.status === 'registered' &&
                        ['completed', 'pending', 'expired'].includes(app.payment_status || '') &&
                        !app.refund && (
                        <button
                          onClick={() => handleCancelApplication(app.id, app.student_name)}
                          disabled={processingId === app.id}
                          className="px-2.5 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded hover:bg-red-100 disabled:opacity-50"
                        >
                          수강취소
                        </button>
                      )}

                      {/* 결제 기한 연장 (입금대기 상태에서만) */}
                      {app.status === 'registered' &&
                        (app.payment_status === 'pending' || app.payment_status === 'expired') && (
                        <button
                          onClick={() => openExtendModal(app)}
                          className="px-2.5 py-1.5 text-xs font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded hover:bg-amber-100"
                        >
                          기한연장
                        </button>
                      )}

                      {/* 수강취소된 신청 복구 (환불된 경우 제외) */}
                      {app.status === 'cancelled' && app.payment_status !== 'refunded' && (
                        <button
                          onClick={() => handleRestoreApplication(app)}
                          disabled={restoringId === app.id}
                          className="px-2.5 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded hover:bg-indigo-100 disabled:opacity-50"
                        >
                          {restoringId === app.id ? '...' : '신청복구'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredApplications.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">
              {searchTerm || selectedProgram !== 'all'
                ? '검색 결과가 없습니다.'
                : activeTab === 'pending'
                ? '입금 대기 중인 신청이 없습니다.'
                : activeTab === 'waiting'
                ? '대기자가 없습니다.'
                : activeTab === 'refund'
                ? '처리할 환불 신청이 없습니다.'
                : activeTab === 'cancelled'
                ? '수강취소된 신청이 없습니다.'
                : '신청 내역이 없습니다.'}
            </p>
          </div>
        )}
      </div>

      {/* 상세 정보 모달 */}
      {showDetailModal && selectedApplication && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900">신청 상세 정보</h2>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* 수강생 정보 */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-3">수강생 정보</h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">이름</span>
                    <span className="text-sm font-medium text-gray-900">{selectedApplication.student_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">아이디</span>
                    <span className="text-sm text-gray-900">{selectedApplication.username}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">연락처</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-900">{selectedApplication.user_phone || '-'}</span>
                      {selectedApplication.user_phone && (
                        <a
                          href={`tel:${selectedApplication.user_phone}`}
                          className="text-indigo-600 hover:text-indigo-700"
                        >
                          <PhoneIcon className="h-4 w-4" />
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">이메일</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-900">{selectedApplication.user_email || '-'}</span>
                      {selectedApplication.user_email && (
                        <a
                          href={`mailto:${selectedApplication.user_email}`}
                          className="text-indigo-600 hover:text-indigo-700"
                        >
                          <EnvelopeIcon className="h-4 w-4" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* 보호자 정보 (미성년자인 경우) */}
              {selectedApplication.guardian_phone && (
                <div>
                  <h3 className="text-sm font-medium text-blue-600 mb-3">보호자 정보</h3>
                  <div className="bg-blue-50 rounded-lg p-4 space-y-2 border border-blue-100">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">보호자 이름</span>
                      <span className="text-sm font-medium text-gray-900">{selectedApplication.guardian_name || '-'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">보호자 연락처</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-900">{selectedApplication.guardian_phone}</span>
                        <a
                          href={`tel:${selectedApplication.guardian_phone}`}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <PhoneIcon className="h-4 w-4" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 신청 정보 */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-3">신청 정보</h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">신청번호</span>
                    <span className="text-sm font-mono text-gray-900">{selectedApplication.application_number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">강좌</span>
                    <span className="text-sm font-medium text-gray-900">{selectedApplication.program_name}</span>
                  </div>
                  {selectedApplication.class_name && selectedApplication.class_name !== '기본반' && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">반</span>
                      <span className="text-sm text-gray-900">{selectedApplication.class_name}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">교육기간</span>
                    <span className="text-sm text-gray-900">
                      {selectedApplication.course_start_date} ~ {selectedApplication.course_end_date}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">신청상태</span>
                    {getStatusBadge(selectedApplication.status)}
                  </div>
                  {selectedApplication.status === 'waiting' && selectedApplication.waiting_number && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">대기번호</span>
                      <span className="text-sm font-medium text-amber-600">#{selectedApplication.waiting_number}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">신청일시</span>
                    <span className="text-sm text-gray-900">
                      {new Date(selectedApplication.created_at).toLocaleString('ko-KR')}
                    </span>
                  </div>
                </div>
              </div>

              {/* 결제 정보 */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-3">결제 정보</h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">결제상태</span>
                    {getPaymentStatusBadge(selectedApplication.payment_status, selectedApplication.payment_deadline)}
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">결제금액</span>
                    <span className="text-sm font-medium text-gray-900">
                      {selectedApplication.payment_amount ? `${selectedApplication.payment_amount.toLocaleString()}원` : '-'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">결제기한</span>
                    <span className="text-sm text-gray-900">
                      {formatDeadline(selectedApplication.payment_deadline)}
                    </span>
                  </div>
                  {selectedApplication.paid_at && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">결제일시</span>
                      <span className="text-sm text-gray-900">
                        {new Date(selectedApplication.paid_at).toLocaleString('ko-KR')}
                      </span>
                    </div>
                  )}
                  {selectedApplication.refund && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">환불상태</span>
                      {getRefundBadge(selectedApplication.refund)}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
              <button
                onClick={() => setShowDetailModal(false)}
                className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 환불 거절 모달 */}
      {showRejectModal && rejectingRefund && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">환불 거절</h3>
              <p className="text-sm text-gray-500 mt-1">
                {rejectingRefund.studentName}님의 환불 신청을 거절합니다.
              </p>
            </div>
            <div className="px-6 py-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                거절 사유 <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="거절 사유를 입력해주세요..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                rows={4}
              />
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectingRefund(null);
                  setRejectionReason('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={handleRejectRefund}
                disabled={processingRefundId === rejectingRefund.id || !rejectionReason.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processingRefundId === rejectingRefund.id ? '처리 중...' : '거절하기'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 관리자 직접 환불 모달 */}
      {showInitiateRefundModal && initiateRefundTarget && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">환불 처리</h3>
              <p className="text-sm text-gray-500 mt-1">
                {initiateRefundTarget.studentName}님의 환불을 직접 처리합니다.
              </p>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <p className="text-sm text-purple-700">
                  <span className="font-medium">프로그램:</span> {initiateRefundTarget.programName}
                </p>
                <p className="text-sm text-purple-700 mt-1">
                  <span className="font-medium">환불 금액:</span> {initiateRefundTarget.amount.toLocaleString()}원
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  환불 사유 <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={initiateRefundReason}
                  onChange={(e) => setInitiateRefundReason(e.target.value)}
                  placeholder="환불 사유를 입력해주세요..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  관리자 메모 <span className="text-gray-400">(선택)</span>
                </label>
                <textarea
                  value={initiateRefundNote}
                  onChange={(e) => setInitiateRefundNote(e.target.value)}
                  placeholder="내부 메모를 입력해주세요..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  rows={2}
                />
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-700">
                  ⚠️ 환불 처리 시 신청이 취소되고 결제 상태가 '환불완료'로 변경됩니다.
                  대기자가 있는 경우 자동으로 승급됩니다.
                </p>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowInitiateRefundModal(false);
                  setInitiateRefundTarget(null);
                  setInitiateRefundReason('');
                  setInitiateRefundNote('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={handleInitiateRefund}
                disabled={processingRefundId === initiateRefundTarget.applicationId || !initiateRefundReason.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processingRefundId === initiateRefundTarget.applicationId ? '처리 중...' : '환불 처리'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 결제기한 연장 모달 */}
      {showExtendModal && extendingApplication && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">결제기한 연장</h3>
              <p className="text-sm text-gray-500 mt-1">
                {extendingApplication.student_name}님의 결제기한을 연장합니다.
              </p>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">현재 결제기한</p>
                <p className="text-sm font-medium text-gray-900">
                  {extendingApplication.payment_deadline
                    ? new Date(extendingApplication.payment_deadline).toLocaleString('ko-KR')
                    : '-'}
                  {extendingApplication.payment_deadline &&
                    new Date(extendingApplication.payment_deadline) < new Date() && (
                    <span className="ml-2 text-red-600">(만료됨)</span>
                  )}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  연장 시간 선택
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {[48, 72, 96, 120, 144].map((hours) => (
                    <button
                      key={hours}
                      onClick={() => setExtendHours(hours)}
                      className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                        extendHours === hours
                          ? 'bg-amber-50 border-amber-300 text-amber-700'
                          : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {hours / 24}일
                    </button>
                  ))}
                </div>
              </div>
              <div className="bg-amber-50 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <span className="font-medium">새 결제기한:</span>{' '}
                  {(() => {
                    const newDate = new Date();
                    newDate.setHours(newDate.getHours() + extendHours);
                    return newDate.toLocaleString('ko-KR');
                  })()}
                </p>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowExtendModal(false);
                  setExtendingApplication(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={handleExtendDeadline}
                disabled={extendingId === extendingApplication.id}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-500 rounded-lg hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {extendingId === extendingApplication.id ? '처리 중...' : '연장하기'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* 일괄 기한연장 모달 */}
      {showBatchExtendModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">일괄 결제기한 연장</h3>
              <p className="text-sm text-gray-500 mt-1">
                신청일 기준으로 대상자를 조회하고, 동일한 납부기한을 일괄 적용합니다.
              </p>
            </div>
            <div className="px-6 py-4 space-y-5 overflow-y-auto flex-1">
              {/* 대상 선택 */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">대상 조건</label>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">신청일:</span>
                    <input
                      type="date"
                      value={batchDateFrom}
                      onChange={(e) => setBatchDateFrom(e.target.value)}
                      className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                    <span className="text-sm text-gray-400">~</span>
                    <input
                      type="date"
                      value={batchDateTo}
                      onChange={(e) => setBatchDateTo(e.target.value)}
                      className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  {selectedProgram !== 'all' && (
                    <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-indigo-50 text-indigo-700 rounded-full border border-indigo-200">
                      {selectedProgram}
                    </span>
                  )}
                  <button
                    onClick={handleBatchSearch}
                    className="px-4 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    대상 조회
                  </button>
                </div>
                {selectedProgram !== 'all' && (
                  <p className="text-xs text-gray-500">* 현재 선택된 강좌 필터가 적용됩니다.</p>
                )}
              </div>

              {/* 대상자 목록 */}
              {batchSearched && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-700">
                      대상자 ({batchTargets.length}명)
                    </label>
                    {batchTargets.length > 0 && (
                      <span className="text-xs text-gray-500">
                        {batchSelectedIds.size}명 선택됨
                      </span>
                    )}
                  </div>
                  {batchTargets.length === 0 ? (
                    <div className="text-center py-6 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-500">해당 조건에 맞는 대상자가 없습니다.</p>
                      <p className="text-xs text-gray-400 mt-1">신청일 범위를 조정하거나 강좌 필터를 확인해주세요.</p>
                    </div>
                  ) : (
                    <div className="border border-gray-200 rounded-lg overflow-hidden max-h-56 overflow-auto">
                      <table className="min-w-full divide-y divide-gray-200 whitespace-nowrap">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left">
                              <input
                                type="checkbox"
                                checked={batchSelectedIds.size === batchTargets.length && batchTargets.length > 0}
                                onChange={toggleBatchSelectAll}
                                className="h-4 w-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                              />
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">수강생</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">신청번호</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">강좌</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">결제상태</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">현재 기한</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">신청일</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                          {batchTargets.map((app) => {
                            const isExpired = app.payment_deadline && new Date(app.payment_deadline) < new Date();
                            return (
                              <tr key={app.id} className={batchSelectedIds.has(app.id) ? 'bg-amber-50/50' : ''}>
                                <td className="px-3 py-2">
                                  <input
                                    type="checkbox"
                                    checked={batchSelectedIds.has(app.id)}
                                    onChange={() => toggleBatchSelect(app.id)}
                                    className="h-4 w-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                                  />
                                </td>
                                <td className="px-3 py-2 text-sm text-gray-900">{app.student_name}</td>
                                <td className="px-3 py-2 text-xs text-gray-500 font-mono">{app.application_number}</td>
                                <td className="px-3 py-2 text-sm text-gray-700 max-w-[140px] truncate">{app.program_name}</td>
                                <td className="px-3 py-2 text-center">
                                  <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${
                                    isExpired || app.payment_status === 'expired'
                                      ? 'bg-red-50 text-red-600 border border-red-200'
                                      : 'bg-amber-50 text-amber-600 border border-amber-200'
                                  }`}>
                                    {app.payment_status === 'expired' || isExpired ? '기한초과' : '입금대기'}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-xs text-gray-500">
                                  {app.payment_deadline ? (
                                    <span className={isExpired ? 'text-red-600' : ''}>
                                      {new Date(app.payment_deadline).toLocaleDateString('ko-KR', {
                                        month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric'
                                      })}
                                    </span>
                                  ) : '-'}
                                </td>
                                <td className="px-3 py-2 text-xs text-gray-500">
                                  {new Date(app.created_at).toLocaleDateString('ko-KR', {
                                    month: 'numeric', day: 'numeric'
                                  })}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* 새 납부기한 지정 */}
              {batchSearched && batchTargets.length > 0 && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">새 납부기한 지정</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="date"
                      value={batchNewDate}
                      onChange={(e) => setBatchNewDate(e.target.value)}
                      className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                    <input
                      type="time"
                      value={batchNewTime}
                      onChange={(e) => setBatchNewTime(e.target.value)}
                      className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  {batchNewDate && batchNewTime && (
                    <div className="bg-amber-50 rounded-lg p-3">
                      <p className="text-sm text-amber-800">
                        <span className="font-medium">선택된 {batchSelectedIds.size}명</span>에게{' '}
                        <span className="font-medium">
                          {new Date(`${batchNewDate}T${batchNewTime}:00+09:00`).toLocaleString('ko-KR', {
                            year: 'numeric', month: 'long', day: 'numeric',
                            hour: '2-digit', minute: '2-digit',
                            timeZone: 'Asia/Seoul'
                          })}
                        </span>
                        {' '}납부기한을 일괄 적용합니다.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowBatchExtendModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                닫기
              </button>
              {batchSearched && batchTargets.length > 0 && (
                <button
                  onClick={handleBatchExtend}
                  disabled={batchProcessing || batchSelectedIds.size === 0 || !batchNewDate || !batchNewTime}
                  className="px-4 py-2 text-sm font-medium text-white bg-amber-500 rounded-lg hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {batchProcessing ? '처리 중...' : `일괄 연장 적용 (${batchSelectedIds.size}명)`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentManagement;
